const uid = Date.now();

// Default settings
const defaults = {
    containerClass: '.bx-validate',
    inputClass: '.bx-input',
    helperClass: '.bx-helper',
    submitClass: '.bx-submit',
};

const utils = {
    isFunction(f) {
        return typeof f === 'function';
    },
    isString(s) {
        return typeof s === 'string';
    },
    isNode(el) {
        return !!(el && el.nodeType && el.nodeType === 1);
    },
    isArray(ar) {
        return Array.isArray(ar);
    },
    isArrayLike(ar) {
        // eslint-disable-next-line no-restricted-globals
        return ar && ar.length && isFinite(ar.length);
    },
    isObject(o) {
        const type = typeof o;
        return (
            type === 'object' &&
            o != null &&
            !utils.isFunction(o) &&
            !utils.isArray(o)
        );
    },
    isNil(o) {
        return o == null;
    },
    has(obj, key) {
        return obj !== null && hasOwnProperty.call(obj, key);
    },
    size(o) {
        if (utils.isObject(o)) {
            if (o.keys) {
                return o.keys().length;
            }
            let l = 0;
            // eslint-disable-next-line no-restricted-syntax
            for (const k in o) {
                if (utils.has(o, k)) {
                    // eslint-disable-next-line no-plusplus
                    l++;
                }
            }
            return l;
        }
        return o.length;
    },
    isNumber(n) {
        // eslint-disable-next-line no-restricted-globals
        return !isNaN(parseFloat(n)) && isFinite(n);
    },
    isCapslock(e) {
        const IS_MAC = /Mac/.test(navigator.platform);
        const {charCode} = e;
        const {shiftKey} = e;
        let capsLock = false;

        if (charCode >= 97 && charCode <= 122) {
            capsLock = shiftKey;
        } else if (charCode >= 65 && charCode <= 90 && !(shiftKey && IS_MAC)) {
            capsLock = !shiftKey;
        }
        return capsLock;
    },
};

/**
 * Each
 *
 * @param collection
 * @param {function} callback
 */
function each(collection, callback) {
    if (
        utils.isNode(collection) ||
        collection === window ||
        collection === document
    ) {
        // eslint-disable-next-line no-param-reassign
        collection = [collection];
    }
    if (!utils.isArrayLike(collection) && !utils.isObject(collection)) {
        // eslint-disable-next-line no-param-reassign
        collection = [collection];
    }
    // eslint-disable-next-line eqeqeq
    if (utils.size(collection) === 0) {
        return;
    }

    if (utils.isArrayLike(collection) && !utils.isObject(collection)) {
        const l = collection.length;
        let i = 0;
        // eslint-disable-next-line no-plusplus
        for (; i < l; i++) {
            if (
                callback.call(collection[i], collection[i], i, collection) ===
                false
            ) {
                break;
            }
        }
    } else if (utils.isObject(collection)) {
        // eslint-disable-next-line no-restricted-syntax
        for (const key in collection) {
            if (utils.has(collection, key)) {
                if (
                    callback.call(
                        collection[key],
                        collection[key],
                        key,
                        collection,
                    ) === false
                ) {
                    break;
                }
            }
        }
    }
}

/**
 * Get nde events
 * return node events and optionally
 * check if the node has already a specific event
 * to avoid duplicated callbacks
 *
 * @param {node} node
 * @param {null} name event name
 * @param {object} fn callback
 * @returns {object}
 */
function getNodeEvents(node, name = null, fn = null) {
    // eslint-disable-next-line no-multi-assign,no-param-reassign
    const cache = (node[uid] = node[uid] || []);
    const data = {all: cache, evt: null, found: null};
    if (name && fn && utils.size(cache) > 0) {
        // eslint-disable-next-line consistent-return
        each(cache, (cl, i) => {
            if (cl.eventName === name && cl.fn.toString() === fn.toString()) {
                data.found = true;
                data.evt = i;
                return false;
            }
        });
    }
    return data;
}

function addEvent(
    eventName,
    {
        onElement,
        withCallback,
        avoidDuplicate = true,
        once = false,
        useCapture = false,
    } = {},
    thisArg,
) {
    let element = onElement || [];
    if (utils.isString(element)) {
        element = document.querySelectorAll(element);
    }

    if (utils.isArray(element)) {
        element.forEach(item => {
            document.querySelectorAll(item);
        });
    }

    function handler(event) {
        if (utils.isFunction(withCallback)) {
            withCallback.call(thisArg, event, this);
        }
        if (once) {
            handler.destroy();
        }
    }

    // eslint-disable-next-line func-names
    handler.destroy = function () {
        // eslint-disable-next-line no-undef,no-shadow
        each(element, el => {
            // eslint-disable-next-line no-undef
            const events = getNodeEvents(el, eventName, handler);
            if (events.found) {
                events.all.splice(events.evt, 1);
            }
            if (el.removeEventListener)
                el.removeEventListener(eventName, handler, useCapture);
        });
    };
    // eslint-disable-next-line no-undef,no-shadow
    each(element, el => {
        // eslint-disable-next-line no-undef
        const events = getNodeEvents(el, eventName, handler);
        if (
            (el.addEventListener && avoidDuplicate && !events.found) ||
            !avoidDuplicate
        ) {
            el.addEventListener(eventName, handler, useCapture);
            events.all.push({eventName, fn: handler});
        }
    });
    return handler;
}

/**
 * BX Validate
 */
const bxValidatePrototype = {
    init(options = {}) {
        this.settings = {...defaults, ...options};
        const {containerClass, inputClass, submitClass} = this.settings;
        if (containerClass) {
            const fields = [...document.querySelectorAll(containerClass)];

            // Check if fields are present
            if (fields.length > 0) {
                // validate input fields on keyup
                this.keyUpInputEvents = addEvent('keyup', {
                    onElement: `${containerClass} input`,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        // ignore tab
                        if (e.keyCode !== 9 && target.dataset.validate === 'true') {
                            this.validateField(target);
                        }
                    },
                });

                // validate textarea fields on keyup
                this.keyUpTextAreaEvents = addEvent('keyup', {
                    onElement: `${containerClass} textarea`,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        // ignore tab
                        if (e.keyCode !== 9 && target.dataset.validate === 'true') {
                            this.validateField(target);
                        }
                    },
                });

                // validate input fields on change
                this.changeInputEvents = addEvent('change', {
                    onElement: `${containerClass} input`,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        if (target.dataset.validate === 'true') {
                            this.validateField(target);
                        }
                    },
                });

                // validate textarea fields on change
                this.changeTextAreaEvents = addEvent('change', {
                    onElement: `${containerClass} textarea`,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        if (target.dataset.validate === 'true') {
                            this.validateField(target);
                        }
                    },
                });


                // float label on focus
                this.focusEvents = addEvent('focus', {
                    onElement: inputClass,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        this.floatLabel(target);
                    },
                });


                // unfloat label on blur
                this.blurEvents = addEvent('blur', {
                    onElement: inputClass,
                    withCallback: (e, target) => {
                        e.preventDefault();
                        this.unFloatLabel(target);
                    },
                });

                // float label if value is present
                fields.forEach(element => {
                    if (
                        element.querySelector(inputClass) && element.querySelector(inputClass).value &&
                        element.querySelector(inputClass).value !== 'default'
                    ) {
                        element.classList.add('is-focused');
                    }
                });
            }
        }
    },

    floatLabel(target) {
        target.parentNode.classList.add('is-focused');
        target.setAttribute(
            'placeholder',
            target.getAttribute('data-placeholder')
                ? target.getAttribute('data-placeholder')
                : // : target.previousElementSibling.innerHTML,
                '',
        );
    },

    unFloatLabel(target) {
        if (!target.value) {
            target.parentNode.classList.remove('is-focused');
        }
        target.removeAttribute('placeholder');
    },

    validateRegex(pattern = '.*\\S.*', value) {
        if (value === 'default' || value === '') {
            return false;
        }
        const regex = new RegExp(pattern);
        return regex.test(value);
    },

    validateRadioAndCheckbox(target) {
        if (target.type === 'radio') {
            const container = target.closest(this.settings.containerClass);
            const radios = container.querySelectorAll('input');
            return [...radios].filter(item => item.checked).length > 0;
        }
        return target.checked;
    },

    validateField(target = null) {
        const helper = target.parentNode.querySelector(this.settings.helperClass);
        const closestContainerClassList = target.closest(this.settings.containerClass).classList;
        const {value, required, dataset} = target;
        const validate = dataset.validate === 'true';
        const isValueValid =
            target.tagName === 'INPUT' &&
            (target.type === 'radio' || target.type === 'checkbox')
                ? this.validateRadioAndCheckbox(target)
                : this.validateRegex(target.pattern, value);
        const isEmailAllowed =
            target.type === 'email' ? this.isEmailAllowed(helper, value) : true;
        const isPasswordValid = target.id === 'password' ? this.isPasswordValid(helper, value) : true;
        const isPasswordConfirmed = target.id === 'password_confirmation' ?
                target.value === target.parentNode.parentNode.querySelector('#password').value : false;

        /* Case: "Confirm-Password" */
        if(target.id === 'password_confirmation') {
            /* Confirm password is not equal to password input */
            if(isPasswordConfirmed) {
                closestContainerClassList.add('is-valid');
                closestContainerClassList.remove('is-invalid');
            } else {
                closestContainerClassList.add('is-invalid');
                closestContainerClassList.remove('is-valid');
                return false;
            }
            return true;
        }
        /* Case: "Password" */
        else if(target.id === 'password') {
            let confirmPasswordWrapper = target.parentNode.parentNode.querySelector('#password_confirmation-wrapper'), confirmPasswordInput, isPasswordEqual;

            if(confirmPasswordWrapper != null) {
                confirmPasswordInput = target.parentNode.parentNode.querySelector('#password_confirmation');
                isPasswordEqual = (confirmPasswordInput.value.length) ? target.value === confirmPasswordInput.value : true;
            }

            /* Password input it not valid and requirements are not matched */
            if (!isValueValid || !isPasswordValid) {
                closestContainerClassList.add('is-invalid');
                closestContainerClassList.remove('is-valid');
                return false;
            }
            /* Password input is valid  */
            else if(isValueValid && isPasswordValid) {
                closestContainerClassList.add('is-valid');
                closestContainerClassList.remove('is-invalid');
            }

            /* Password input is valid but not equal to confirmPassword and therefore the confirmPassword field + wrapper are marked as invalid */
            if (confirmPasswordWrapper != null && isValueValid && isPasswordValid && !isPasswordEqual) {
                confirmPasswordWrapper.classList.add('is-invalid');
                confirmPasswordWrapper.classList.remove('is-valid');
                confirmPasswordInput.classList.add('is-invalid');
                confirmPasswordInput.classList.remove('is-valid');
            }

            if (confirmPasswordWrapper != null && isValueValid && isPasswordValid && isPasswordEqual) {
                confirmPasswordWrapper.classList.add('is-valid');
                confirmPasswordWrapper.classList.remove('is-invalid');
                confirmPasswordInput.classList.add('is-valid');
                confirmPasswordInput.classList.remove('is-invalid');
            }

            return true;
        }
        /* Case: "All others" */
        else {
            if ((!isValueValid && !isEmailAllowed) || (required || validate)) {
                closestContainerClassList.add('is-invalid');
                closestContainerClassList.remove('is-valid');
            }

            if ((isValueValid && isEmailAllowed) || (!required && !validate)) {
                closestContainerClassList.add('is-valid');
                closestContainerClassList.remove('is-invalid');
                return true;
            }
        }

        return false;
    },

    isEmailAllowed(helper, value) {
        const helperElement = helper;
        const dataSetBlocked = helper.dataset.blocked;
        const dataSetInvalid = helper.dataset.invalid;
        const blockedDomain = window.blockedDomains.find(e => e === value.split('@')[1]);
        if (blockedDomain) {
            /* Set first the text */
            helperElement.innerText = dataSetInvalid;
            /* Next replace in text the domain string by forbidden domain name */
            helperElement.innerText = dataSetBlocked.replace(
                '%domain%',
                blockedDomain,
            );
            return false;
        }
        /* Reset text */
        helperElement.innerText = '';

        return true;
    },

    isPasswordValid(helper, value) {
        // capslock
        const capsLockHelper = helper.querySelector('.pw-caps');
        // length
        const lengthHelper = helper.querySelector('.pw-length');
        const lengthProgressBar = lengthHelper.querySelector('.progress-bar');
        // capital characters
        const capitalCharactersHelper = helper.querySelector('.pw-capital');
        const capitalCharactersProgressBar = capitalCharactersHelper.querySelector(
            '.progress-bar',
        );
        const capitalCharacterRegex = /[A-Z]/;
        // lower characters
        const lowerCharactersHelper = helper.querySelector('.pw-lower');
        const lowerCharactersProgressBar = lowerCharactersHelper.querySelector(
            '.progress-bar',
        );
        const lowerCharacterRegex = /[a-z]/;
        // digits
        const digitsHelper = helper.querySelector('.pw-digits');
        const digitsProgressBar = digitsHelper.querySelector('.progress-bar');
        const digitsRegex = /\d/;
        // special characters
        const specialCharactersHelper = helper.querySelector('.pw-special-character');
        const specialCharactersProgressBar = specialCharactersHelper.querySelector('.progress-bar');
        const specialCharacterRegex = /(?=.*[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7E\x80-\xFF])/;

        // show capsLock helper
        document.addEventListener('keypress', e => {
            capsLockHelper.style.display = utils.isCapslock(e)
                ? 'flex'
                : 'none';
        });

        // animate pw length
        lengthProgressBar.style.backgroundColor =
            value.length > 7 ? '#66b245' : '#C81919';
        lengthProgressBar.style.width =
            value.length === 0 ? '6.25%' : `${(value.length / 8) * 100}%`;
        lengthHelper.style.color =
            value.length > 7 ? '#66b245' : '#C81919';

        // animate capital characters
        capitalCharactersProgressBar.style.width = capitalCharacterRegex.test(value)
            ? '100%'
            : '6.25%';
        capitalCharactersProgressBar.style.backgroundColor = capitalCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';
        capitalCharactersHelper.style.color = capitalCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';

        // animate lower characters
        lowerCharactersProgressBar.style.width = lowerCharacterRegex.test(value)
            ? '100%'
            : '6.25%';
        lowerCharactersProgressBar.style.backgroundColor = lowerCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';
        lowerCharactersHelper.style.color = lowerCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';

        // animate digits
        digitsProgressBar.style.width = digitsRegex.test(value)
            ? '100%'
            : '6.25%';
        digitsProgressBar.style.backgroundColor = digitsRegex.test(value)
            ? '#66b245'
            : '#C81919';
        digitsHelper.style.color = digitsRegex.test(value)
            ? '#66b245'
            : '#C81919';

        // animate special characters
        specialCharactersProgressBar.style.width = specialCharacterRegex.test(value)
            ? '100%'
            : '6.25%';
        specialCharactersProgressBar.style.backgroundColor = specialCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';
        specialCharactersHelper.style.color = specialCharacterRegex.test(value)
            ? '#66b245'
            : '#C81919';

        return true;
    },
};

const bxValidateInstance = () => Object.create(bxValidatePrototype);

export default function bxValidate(options = {}) {
    const instance = bxValidateInstance(options);
    instance.init();
    return instance;
}
